import { io } from 'socket.io-client';
import type {
    SocketOptions,
    ConnectionStatus,
    UserInfo,
    JoinResourceOptions,
    ResourceJoinedData,
    ResourceLeftData,
    UserJoinedData,
    UserLeftData,
    AllUsersData,
    AllUsersDataHierarchical,
    AllUsersDataFlat,
    SubResourceData,
    LockAcquiredResponse,
    LockDeniedResponse,
    LockReleasedResponse,
    LockExtendedResponse,
    ReleasePendingData,
    ReleaseRequestedData,
    RequestAcceptedData,
    RequestDeniedData,
    RequestTimeoutData,
    ResourceUpdatedData,
    SocketCallbacks,
    ConnectionChangeCallback,
    ResourceJoinedCallback,
    ResourceLeftCallback,
    UserJoinedCallback,
    UserLeftCallback,
    AllUsersCallback,
    UsersChangeCallback,
    LockAcquiredCallback,
    LockDeniedCallback,
    LockReleasedCallback,
    LockExtendedCallback,
    ReleasePendingCallback,
    ReleaseRequestedCallback,
    RequestAcceptedCallback,
    RequestDeniedCallback,
    RequestTimeoutCallback,
    ResourceUpdatedCallback,
    LockForceReleasedResponse,
    LockForceReleasedCallback,
    LockStatusResponse,
    LockStatusCallback,
    EventCallback,
    ErrorCallback,
    SocketError
} from '../types/socket.types';


function getSocketBaseUrl() {
    // Se siamo in development e connesso a localhost, usa localhost:3000
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return 'http://localhost:3002';
    }

    // Per test/prod: usa lo stesso dominio e protocollo (passa per nginx)
    // es: https://api.test.com → https://api.test.com (nginx proxya /ws/ al backend)
    return `${window.location.protocol}//${window.location.host}`;
}


export const ResourceType = {
    SURGERY_MANAGEMENT: 'surgery-management',
    SURGERY_REQUEST: 'surgery-request',
    SURGERY: 'surgery', // legacy
    OUTPATIENT_VISIT: 'outpatient-visit',
    PATIENT: 'patient'
};

export const SurgeryTabId = {
    DATA: 'data-tab',
    DETAIL: 'detail-tab',
    TECHNICAL_DATA: 'technical-data-tab',
    OPERATORS: 'operators-tab',
    DIAGNOSIS: 'diagnosis-tab',
    EXAMS: 'exams-tab',
    INFUSIONS: 'infusions-tab',
    PROCEDURES: 'procedures-tab',
    CHIRURGICAL_ACT: 'chirurgicalact-tab',
    MATERIALS: 'materials-tab',
    IMPLANTABLES: 'implantables-tab',
    PATH: 'path-tab',
    ANESTHESIS_FOLDER: 'anesthesis-folder-tab',
    NURSING_FOLDER: 'nursing-folder-tab',
    ANESTHESIS: 'anesthesis-tab',
    ANESTHESIS_PATH: 'anesthesis-path-tab',
    PATIENT_PATH: 'patient-path-tab',
    VALIDATION: 'validation-tab'
};


export const ConnectionStatusEnum = {
    DISCONNECTED: 'disconnected' as ConnectionStatus,
    CONNECTING: 'connecting' as ConnectionStatus,
    CONNECTED: 'connected' as ConnectionStatus,
    ERROR: 'error' as ConnectionStatus
};

class SocketService {
    // Socket connection
    private socket: any = null;
    private connectionStatus: ConnectionStatus = 'disconnected';

    // Retry mechanism
    private retryCount: number = 0;
    private maxRetries: number = 5;
    private retryDelays: number[] = [1000, 2000, 4000, 8000, 16000];
    private retryTimeout: NodeJS.Timeout | null = null;
    private autoReconnect: boolean = true;

    // Current resource context
    private currentResourceType: string | null = null;
    private currentResourceUuid: string | null = null;
    private currentResourceId: string | null = null;
    private currentUser: UserInfo | null = null;
    public activeUsers: UserInfo[] = [];
    public lockedTabs: Map<string, any> = new Map();

    // Legacy callbacks (object-based pattern)
    public callbacks: SocketCallbacks = {
        onConnectionChange: null,
        onAuthenticated: null,
        onResourceJoined: null,
        onResourceLeft: null,
        onUserJoined: null,
        onUserLeft: null,
        onAllUsersUpdate: null,
        onTabLocked: null,
        onTabLockDenied: null,
        onTabUnlocked: null,
        onTabForceReleased: null,
        onLockStatus: null,
        onError: null
    };

    // New pattern callbacks (direct properties)
    public onConnectionChange: ConnectionChangeCallback | null = null;
    public onUsersChange: UsersChangeCallback | null = null;
    public onEvent: EventCallback | null = null;
    public onCrossTabUserJoined: UserJoinedCallback | null = null;
    public onCrossTabUserLeft: UserLeftCallback | null = null;

    // Lock callbacks (align with BE naming: lock:acquired, lock:denied, etc.)
    public onLockAcquired: LockAcquiredCallback | null = null;
    public onLockDenied: LockDeniedCallback | null = null;
    public onLockReleased: LockReleasedCallback | null = null;
    public onLockExtended: LockExtendedCallback | null = null;

    // Force-release callback (Monitor forced release of child locks)
    public onLockForceReleased: LockForceReleasedCallback | null = null;

    // Lock status callback (parent/child lock state updates)
    public onLockStatus: LockStatusCallback | null = null;

    // Resource join success callback (auto-lock trigger)
    public onResourceJoinedSuccess: ((data: ResourceJoinedData) => void) | null = null;

    // Users list callback (sync all users in current resource)
    public onAllUsers: AllUsersCallback | null = null;

    // Lock request/release callbacks (collaborative workflow)
    public onReleasePending: ReleasePendingCallback | null = null;
    public onReleaseRequested: ReleaseRequestedCallback | null = null;
    public onRequestAccepted: RequestAcceptedCallback | null = null;
    public onRequestDenied: RequestDeniedCallback | null = null;
    public onRequestTimeout: RequestTimeoutCallback | null = null;

    // Data sync callback (resource updated by another user)
    public onResourceUpdated: ResourceUpdatedCallback | null = null;

    // Additional properties for mock fallback
    private hasReceivedAllUsers: boolean = false;
    private mockDataFallbackTimer: NodeJS.Timeout | null = null;

    constructor() {
        this.socket = null;
        this.connectionStatus = ConnectionStatusEnum.DISCONNECTED;
        this.retryCount = 0;
        this.maxRetries = 5;
        this.retryDelays = [1000, 2000, 4000, 8000, 16000];
        this.retryTimeout = null;
        this.autoReconnect = true;

        // Current resource context
        this.currentResourceType = null;
        this.currentResourceUuid = null;
        this.currentResourceId = null;  // For new pattern
        this.currentUser = null;        // For new pattern
        this.activeUsers = [];          // For new pattern
        this.lockedTabs = new Map();

        // Callbacks per eventi socket
        this.callbacks = {
            onConnectionChange: null,
            onAuthenticated: null,
            onResourceJoined: null,
            onResourceLeft: null,
            onUserJoined: null,
            onUserLeft: null,
            onAllUsersUpdate: null,
            onTabLocked: null,
            onTabLockDenied: null,
            onTabUnlocked: null,
            onTabForceReleased: null,
            onLockStatus: null,
            onError: null
        };

        // New pattern callbacks (direct properties)
        this.onConnectionChange = null;
        this.onUsersChange = null;
        this.onEvent = null;
        this.onCrossTabUserJoined = null;
        this.onCrossTabUserLeft = null;

        // Lock callbacks (align with BE naming: lock:acquired, lock:denied, etc.)
        this.onLockAcquired = null;     // User acquired exclusive lock
        this.onLockDenied = null;       // Lock acquisition denied (already locked)
        this.onLockReleased = null;     // Lock was released
        this.onLockExtended = null;     // Lock TTL extended via heartbeat

        // Users list callback (sync all users in current resource)
        this.onAllUsers = null;         // Complete list of users in resource

        // Lock request/release callbacks (collaborative workflow)
        this.onReleasePending = null;       // Release request sent (requester notification)
        this.onReleaseRequested = null;     // Release request received (lock holder notification)
        this.onRequestAccepted = null;      // Release request accepted (requester notification)
        this.onRequestDenied = null;        // Release request denied (requester notification)
        this.onRequestTimeout = null;       // Release request timeout (both parties notification)
        // Data sync callback (resource updated by another user)
        this.onResourceUpdated = null;  // {uuid, currentRevision, updatedTab, changesSummary, timestamp}

        console.log('[SocketService] Service initialized');
    }


    on(event: string, callback: any): void {
        const capitalizedEvent = `on${this._capitalize(event)}`;
        if (this.callbacks.hasOwnProperty(capitalizedEvent)) {
            (this.callbacks as any)[capitalizedEvent] = callback;
            console.log(`Registered callback for: ${event} → will trigger on backend event`);
        } else {
            console.warn(`Unknown event: ${event}. Available: ${Object.keys(this.callbacks).map(k => k.replace('on', '')).join(', ')}`);
        }
    }

    connect(options: SocketOptions = {}): void {
        // Prevent multiple simultaneous connection attempts
        if (this.socket && this.socket.connected) {
            console.log('[SocketService] Already connected');
            return;
        }

        if (this.connectionStatus === 'connecting') {
            console.log('[SocketService] Connection already in progress');
            return;
        }

        const {
            url = getSocketBaseUrl(),
            path = '/ws/socket.io',
            namespace = '/collaboration',
            autoReconnect = true
        } = options;

        this.autoReconnect = autoReconnect;

        this._updateConnectionStatus(ConnectionStatusEnum.CONNECTING);

        // Get user info from localStorage
        const userInfoStr = localStorage.getItem('userInfo');
        let username = 'anonymous';

        if (userInfoStr) {
            try {
                const userInfo = JSON.parse(userInfoStr);
                username = userInfo.userInfo?.userName || userInfo.userName || 'anonymous';
            } catch (error) {
                console.warn('[SocketService] Failed to parse userInfo from localStorage:', error);
            }
        }

        console.log('[SocketService] Connecting with username:', username);

        try {

            this.socket = io(`${url}${namespace}`, {
                path: path,
                transports: ['websocket', 'polling'],
                auth: {
                    token: null,
                    userInfo: {
                        userId: username,
                        username: username,

                    }
                },
                reconnection: false,

            });

            // Log ALL events received (debug catchall)
            this.socket.onAny((eventName: string, ...args: any[]) => {
                console.log(`[SocketService] EVENT RECEIVED: "${eventName}"`, args.length > 0 ? args[0] : '(no data)');
            });

            // List all registered listeners after setup
            setTimeout(() => {
                const listeners = this.socket?.listeners('resource:all_users');
            }, 1000);

            this._setupSocketListeners();

        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            console.error(' Connection failed:', error);
            this._updateConnectionStatus(ConnectionStatusEnum.ERROR);
            this._notifyError('CONNECTION_ERROR', errorMessage);

            // Only schedule retry if we haven't exceeded max retries
            if (this.autoReconnect && this.retryCount < this.maxRetries) {
                this._scheduleRetry();
            }
        }
    }

    _setupSocketListeners() {
        if (!this.socket) return;

        this.socket.on('connect', () => {

            console.log('║ SOCKET CONNECTED║');

            this._updateConnectionStatus(ConnectionStatusEnum.CONNECTED);
            this.retryCount = 0; // Reset retry counter
        });

        this.socket.on('disconnect', (reason: string) => {
            console.log('Disconnected:', reason);
            this._updateConnectionStatus(ConnectionStatusEnum.DISCONNECTED);

            // Clean up local lock state on unexpected disconnect
            // The server should handle lock cleanup via handleDisconnect() hook
            if (this.lockedTabs.size > 0) {
                this.lockedTabs.clear();
            }

            // Auto-reconnect se abilitato e non è una disconnessione volontaria
            if (this.autoReconnect && reason !== 'io client disconnect') {
                this._scheduleRetry();
            }
        });

        this.socket.on('connect_error', (error: any) => {
            console.log('║ CONNECTION ERROR  ║');
            // Log connection details for debugging
            if (error.description) {
                const description = String(error.description);
                console.log('║  Details:', description.substring(0, 48).padEnd(48), '║');
            }

            this._updateConnectionStatus(ConnectionStatusEnum.ERROR);
            this._notifyError('CONNECTION_ERROR', error.message);

            // Only retry if autoReconnect is enabled and we haven't exceeded max retries
            if (this.autoReconnect && this.retryCount < this.maxRetries) {
                this._scheduleRetry();
            } else if (this.retryCount >= this.maxRetries) {
                console.error('[SocketService] Max retries reached. Stopping reconnection attempts.');
                this.autoReconnect = false;
            }
        });

        this.socket.on('error', (error: any) => {
            console.error('Socket error:', error);
            this._notifyError('SOCKET_ERROR', error);
        });

        this.socket.on('connection:warning', (data: any) => {
            console.warn('[Connection warning:', data.message);
            console.warn('Usage:', `${data.percentageUsed}%`);
        });

        // ==================== RESOURCE JOIN/LEAVE ====================

        this.socket.on('resource:joined', (data: ResourceJoinedData) => {

            console.log('║ SOCKET RESOURCE JOINED - Response Received║');

            if (data.success === false) {
                this._notifyError('JOIN_FAILED', data.message || 'Join failed');
                return;
            }

            // Normalize the data to standard format
            const resourceType = data.resourceType || 'surgery-management';
            const resourceUuid = data.resourceUuid || data.resourceId;
            const roomId = data.roomId || data.resourceId;
            const users = data.users || data.currentUsers || [];

            if (users.length > 0) {
                users.forEach((u: UserInfo) => {
                    const userInfo = `${u.username} (${u.role || 'viewer'})`;
                    console.log('║    • ', userInfo.substring(0, 55).padEnd(55), '║');
                });
            }

            this.currentResourceType = resourceType;
            this.currentResourceUuid = resourceUuid || null;

            if (this.callbacks.onResourceJoined) {
                // Pass normalized data
                this.callbacks.onResourceJoined({
                    resourceType,
                    resourceUuid,
                    roomId,
                    currentUsers: users,
                    success: true
                });
            }

            // NEW pattern: invoke success callback for auto-lock trigger
            if (this.onResourceJoinedSuccess) {
                this.onResourceJoinedSuccess({
                    resourceType,
                    resourceUuid,
                    roomId,
                    currentUsers: users,
                    success: true
                });
            }
        });

        this.socket.on('resource:left', (data: ResourceLeftData) => {
            console.log('Left resource:', data.resourceUuid);

            this.currentResourceType = null;
            this.currentResourceUuid = null;
            this.lockedTabs.clear();

            if (this.callbacks.onResourceLeft) {
                this.callbacks.onResourceLeft(data);
            }
        });

        // ==================== USER COLLABORATION ====================

        this.socket.on('user:joined', (data: UserJoinedData) => {
            console.log('User DATA:', data);
            // Update activeUsers array if same resource
            if (data.resourceId === this.currentResourceId) {
                const existingIndex = this.activeUsers.findIndex(u => u.userId === data.userId);
                if (existingIndex === -1) {
                    this.activeUsers.push({
                        userId: data.userId,
                        username: data.username,
                        email: data.email,
                        socketId: data.socketId,
                        mode: data.mode,
                        joinedAt: data.joinedAt
                    });
                    // Trigger callback for UI update
                    if (this.onUsersChange) {
                        this.onUsersChange(this.activeUsers);
                    }
                }
            }

            if (this.callbacks.onUserJoined) {
                this.callbacks.onUserJoined(data);
            }
        });

        this.socket.on('user:left', (data: UserLeftData) => {
            // Remove user from activeUsers array if same resource
            if (data.resourceId === this.currentResourceId) {
                const userIndex = this.activeUsers.findIndex(u => u.userId === data.userId);
                if (userIndex !== -1) {

                    // Trigger callback for UI update
                    if (this.onUsersChange) {
                        console.log('[SocketService] Calling onUsersChange callback after user left');
                        this.onUsersChange(this.activeUsers);
                    }
                } else {
                    console.warn('User not found in activeUsers:', data.userId);
                }
            }

            // Trigger legacy callback
            if (this.callbacks.onUserLeft) {
                this.callbacks.onUserLeft(data);
            }
        });


        // this.socket.on('resource:all_users', (data: AllUsersData) => {

        //     this.hasReceivedAllUsers = true;
        //     if (this.mockDataFallbackTimer) {
        //         console.log('[SocketService] Canceling mock data fallback (real event received)');
        //         clearTimeout(this.mockDataFallbackTimer);
        //         this.mockDataFallbackTimer = null;
        //     }

        //     // NEW FORMAT: Hierarchical structure with parent/sub-resources
        //     if (data && 'parentResourceId' in data && 'subResources' in data) {
        //         const hierarchicalData = data as AllUsersDataHierarchical;
        //         const allUsers: UserInfo[] = [];
        //         hierarchicalData.subResources.forEach((subResource: SubResourceData) => {
        //             subResource.users.forEach((user: UserInfo) => {
        //                 allUsers.push({
        //                     ...user,
        //                     tabId: subResource.subResourceId // Add tab context
        //                 });
        //             });
        //         });

        //         // Notify via NEW pattern callback (with full hierarchical data)
        //         if (this.onAllUsers) {
        //             this.onAllUsers(data);
        //         } else {
        //             console.warn('No onAllUsers callback registered! (hierarchical format)');
        //         }

        //         // OLD pattern callback (with flattened users array)
        //         if (this.callbacks.onAllUsersUpdate) {
        //             this.callbacks.onAllUsersUpdate(allUsers);
        //         }
        //     }
        //     // OLD FORMAT: Simple users array (fallback for backward compatibility)
        //     else if (data && data.users) {
        //         const users = data.users || [];
        //         // Notify via NEW pattern callback
        //         if (this.onAllUsers) {
        //             this.onAllUsers(users);
        //         }

        //         // OLD pattern callback
        //         if (this.callbacks.onAllUsersUpdate) {
        //             this.callbacks.onAllUsersUpdate(users);
        //         }
        //     }
        //     else {

        //         console.log(' Data received:', data);

        //     }

        //     // FALLBACK: If resource:joined didn't fire, use this as the initial user list
        //     // This handles cases where backend only sends resource:all_users
        //     if (this.callbacks.onResourceJoined && 'users' in data && data.users) {
        //         const flatData = data as AllUsersDataFlat;
        //         console.log('Fallback: Using resource:all_users data as resource:joined response');
        //         this.callbacks.onResourceJoined({
        //             resourceType: flatData.resourceType || 'surgery-management',
        //             resourceUuid: flatData.parentResourceId || flatData.resourceId || flatData.resourceUuid || '',
        //             roomId: flatData.parentResourceId || flatData.resourceId || flatData.resourceUuid || '',
        //             currentUsers: flatData.users,
        //             success: true
        //         });
        //     }
        // });

        // ==================== LOCK EVENTS (Align with BE) ====================

        this.socket.on('lock:acquired', (response: LockAcquiredResponse) => {


            // Notify via NEW pattern callback
            if (this.onLockAcquired) {
                this.onLockAcquired(response);
            }

            // Legacy callback support
            if (this.callbacks.onTabLocked) {
                this.callbacks.onTabLocked(response);
            }
        });

        this.socket.on('lock:denied', (response: LockDeniedResponse) => {

            console.warn('Locked by:', response.lockedBy?.username);


            // Notify via NEW pattern callback
            if (this.onLockDenied) {
                this.onLockDenied(response);
            }

            // Legacy callback support
            if (this.callbacks.onTabLockDenied) {
                this.callbacks.onTabLockDenied(response);
            }
        });

        this.socket.on('lock:released', (response: LockReleasedResponse) => {
            console.log(' ON Lock released!');

            // Notify via NEW pattern callback
            if (this.onLockReleased) {
                this.onLockReleased(response);
            }

            // Legacy callback support
            if (this.callbacks.onTabUnlocked) {
                this.callbacks.onTabUnlocked(response);
            }
        });

        this.socket.on('lock:extended', (response: LockExtendedResponse) => {

            // Notify via NEW pattern callback
            if (this.onLockExtended) {
                this.onLockExtended(response);
            }
        });

        // ==================== LOCK REQUEST/RELEASE EVENTS ====================

        this.socket.on('lock:release_pending', (response: ReleasePendingData) => {
            console.log('[SocketService] Lock release request pending:', response);
            if (this.onReleasePending) {
                this.onReleasePending(response);
            }
        });

        this.socket.on('lock:release_requested', (response: ReleaseRequestedData) => {
            console.log('[SocketService] Lock release requested (lock holder notification):', response);
            if (this.onReleaseRequested) {
                this.onReleaseRequested(response);
            }
        });

        this.socket.on('lock:request_accepted', (response: RequestAcceptedData) => {
            console.log('[SocketService] Lock release request accepted:', response);
            if (this.onRequestAccepted) {
                this.onRequestAccepted(response);
            }
        });

        this.socket.on('lock:request_denied', (response: RequestDeniedData) => {
            console.log('[SocketService] Lock release request denied:', response);
            if (this.onRequestDenied) {
                this.onRequestDenied(response);
            }
        });

        this.socket.on('lock:request_timeout', (response: RequestTimeoutData) => {
            console.log('[SocketService] Lock release request timeout:', response);
            if (this.onRequestTimeout) {
                this.onRequestTimeout(response);
            }
        });

        // ==================== FORCE-RELEASE (Monitor parent lock) ====================
        this.socket.on('lock:force_released', (response: LockForceReleasedResponse) => {
            console.log('[SocketService] lock:force_released received:', response);

            if (!response || !response.resourceId) {
                console.warn('[SocketService] lock:force_released payload missing resourceId', response);
                return;
            }

            const resourceId = response.resourceId;

            // Mark the resource/tab as forcibly released (locked by monitor/parent)
            const lockInfo = {
                forced: true,
                forcedBy: response.forcedBy || null,
                newOwner: response.newOwner || null,
                reason: response.reason || 'force_released',
                timestamp: response.timestamp || new Date().toISOString()
            };

            try {
                this.lockedTabs.set(resourceId, lockInfo);
            } catch (err) {
                console.error('[SocketService] Failed to update lockedTabs for force_released:', err);
            }

            // NEW pattern callback
            if (this.onLockForceReleased) {
                this.onLockForceReleased(response);
            }

            // Legacy callback support
            if (this.callbacks.onTabForceReleased) {
                this.callbacks.onTabForceReleased(response as any);
            }

            // Generic event/emitter
            if (this.onEvent) {
                this.onEvent('lock:force_released', 'Resource forcibly released by parent/monitor', response);
            }
        });

        // ==================== LOCK STATUS (parent/child) ====================
        this.socket.on('lock:status', (response: LockStatusResponse) => {
            console.log('[SocketService] lock:status received:', response);

            if (!response || !response.resourceId) {
                console.warn('[SocketService] lock:status payload missing resourceId', response);
                return;
            }

            const { resourceId, locked, lockedBy } = response;

            // Distinguish parent vs child by counting parts (parent: 'type:uuid', child: 'type:uuid:tab')
            const parts = resourceId.split(':');

            try {
                if (parts.length === 2) {
                    // Parent resource lock status
                    console.log(`[SocketService] Parent lock status - resourceId: ${resourceId}, locked: ${locked}, lockedBy: ${lockedBy?.username || 'none'}`);
                    if (locked) {
                        // Mark parent as locked
                        this.lockedTabs.set(resourceId, {
                            parentLocked: true,
                            lockedBy: response.lockedBy || null,
                            timestamp: response.timestamp || new Date().toISOString()
                        });
                    } else {
                        // Parent unlocked -> remove parent entry
                        if (this.lockedTabs.has(resourceId)) {
                            this.lockedTabs.delete(resourceId);
                        }
                    }
                } else {
                    // Child resource (tab) status
                    console.log(`[SocketService] Child lock status - resourceId: ${resourceId}, locked: ${locked}, lockedBy: ${lockedBy?.username || 'none'}`);
                    if (locked) {
                        // Child locked (someone holds it) -> store simple lock info
                        this.lockedTabs.set(resourceId, {
                            lockedBy: response.lockedBy || null,
                            timestamp: response.timestamp || new Date().toISOString()
                        });
                    } else {
                        // Child unlocked -> ensure any local mark is removed
                        if (this.lockedTabs.has(resourceId)) {
                            this.lockedTabs.delete(resourceId);
                        }
                    }
                }
            } catch (err) {
                console.error('[SocketService] Error handling lock:status', err);
            }

            // NEW pattern callback
            if (this.onLockStatus) {
                console.log('[SocketService] Invoking onLockStatus callback');
                this.onLockStatus(response);
            }

            // Legacy callback support
            if (this.callbacks.onLockStatus) {
                console.log('[SocketService] Invoking legacy onLockStatus callback');
                this.callbacks.onLockStatus(response as any);
            }

            // Generic event/emitter
            if (this.onEvent) {
                this.onEvent('lock:status', 'Lock status update received', response);
            }
        });
        // ==================== RESOURCE DATA UPDATES ====================

        // this.socket.on('resource:updated', (data: ResourceUpdatedData) => {
        //     // Notify via callback for cross-tab data sync
        //     if (this.onResourceUpdated) {
        //         this.onResourceUpdated(data);
        //     }
        // });
    }


    addEvent(type: string, message: string, data: any = null): void {
        if (this.onEvent) {
            this.onEvent(type, message, data);
        }
    }


    /**
     * Join a una risorsa (Generic Resource API)
     * 
     * @param {string} resourceType - Tipo risorsa (es: 'surgery-management')
     * @param {string} resourceUuid - UUID della risorsa
     * @param {string} role - Ruolo utente: 'editor' o 'viewer' (default: 'editor')
     */
    joinResource(resourceType: string, resourceUuid: string, options: JoinResourceOptions | string = {}): void {
        if (!this.socket || !this.socket.connected) {
            console.error(' Cannot join resource: socket not connected');
            console.log(' Socket status:', { connected: this.socket?.connected, socketId: this.socket?.id });
            return;
        }

        const mode = typeof options === 'string' ? options : options.mode || 'viewer';
        const tabId = typeof options === 'object' ? options.tabId : null;
        const subResourceId = typeof options === 'object' ? options.resourceId : null;

        // Backend si aspetta: resourceId = "resourceType:uuid" OR "resourceType:uuid:tabId"
        let resourceId = `${resourceType}:${resourceUuid}`;
        if (subResourceId) {
            // Se è specificato un tab, aggiungi al resourceId (es. "surgery-management:uuid:data-tab")
            resourceId = subResourceId;
        } else if (tabId) {
            // Fallback: costruisci resourceId con tabId
            resourceId = `${resourceType}:${resourceUuid}:${tabId}`;
        }

        const joinPayload = {
            resourceId: resourceId,
            mode: 'editor',
            resourceType: 'surgery-management',
            isMonitor: true,
        };

        console.log(' JOIN PAYLOAD:', JSON.stringify(joinPayload, null, 2));

        this.socket.emit('resource:join', joinPayload);

        // Update local context
        this.currentResourceUuid = resourceUuid;
        if (tabId) {
            this.currentResourceId = resourceId;
        }
    }

    leaveResource(resourceType?: string, resourceUuid?: string): void {
        if (!this.socket || !this.socket.connected) {
            console.warn('Cannot leave resource: socket not connected');
            return;
        }

        // Use provided params or fallback to current context
        const type = resourceType || this.currentResourceType || 'surgery-management';
        const uuid = resourceUuid || this.currentResourceUuid;

        if (!uuid) {
            console.warn('[SocketService] Cannot leave resource: no resourceUuid provided or stored');
            return;
        }

        console.log('[🔌 lock:release] Leaving resource... Type:', type, 'UUID:', uuid);

        this.socket.emit('resource:leave', {
            resourceType: type,
            resourceUuid: uuid,
            resourceId: `${type}:${uuid}`  // Include explicit resourceId for clarity
        });

        // Clear current context
        this.currentResourceUuid = null;
        this.currentResourceType = null;
        this.currentResourceId = null;
    }

    lockTab(resourceType: string, resourceUuid: string, tabId: string): void {
        if (!this.socket || !this.socket.connected) {
            console.error('Cannot lock tab: socket not connected');
            return;
        }

        console.log('lock:acquire lock:acquire Tab ID:', tabId);

        this.socket.emit('lock:acquire', {
            resourceId: `${'surgery-management'}:${resourceUuid}:${tabId}`,
            mode: 'editor',  // User is in edit mode
            ttl: 300000  // 5 minutes default TTL
        });
    }

    lockResource(resourceType: string, resourceUuid: string, mode: 'editor' | 'viewer' = 'viewer'): void {
        if (!this.socket || !this.socket.connected) {
            console.error('Cannot lock resource: socket not connected');
            return;
        }

        console.log(`lock:acquire parent resource UUID: ${resourceUuid}, mode: ${mode}`);

        this.socket.emit('lock:acquire', {
            resourceId: `${'surgery-management'}:${resourceUuid}`,
            mode: mode,
            ttl: 300000  // 5 minutes default TTL
        });
    }

    unlockTab(resourceType: string, resourceUuid: string, tabId: string): void {
        if (!this.socket || !this.socket.connected) {
            return;
        }

        console.log('[Unlocking tab...', tabId);

        this.socket.emit('lock:release', {
            resourceId: `${'surgery-management'}:${resourceUuid}:${tabId}`,
            mode: 'viewer'  // User is no longer editing
        });
    }

    unlockResource(resourceType: string, resourceUuid: string, mode: 'editor' | 'viewer' = 'viewer'): void {
        if (!this.socket || !this.socket.connected) {
            return;
        }

        console.log('[Unlocking parent resource...', resourceUuid, 'mode:', mode);

        this.socket.emit('lock:release', {
            resourceId: `${'surgery-management'}:${resourceUuid}`,
            mode: mode
        });
    }

    extendTabLock(resourceType: string, resourceUuid: string, tabId: string, mode: 'editor' | 'viewer'): void {
        if (!this.socket || !this.socket.connected) {
            console.warn('Cannot extend lock: socket not connected');
            return;
        }
        this.socket.emit('lock:extend', {
            resourceId: `${'surgery-management'}:${resourceUuid}:${tabId}`,
            mode: mode,  // Still editing
            ttl: 300000  // Extend for 5 more minutes
        });
    }

    isTabLocked(tabId: string): boolean {
        return this.lockedTabs.has(tabId);
    }

    getTabLockInfo(tabId: string): any | null {
        return this.lockedTabs.get(tabId) || null;
    }

    // Helper: check if parent resource is locked
    isParentLocked(resourceUuid: string): boolean {
        const parentId = `surgery-management:${resourceUuid}`;
        const info = this.lockedTabs.get(parentId);
        return info?.parentLocked === true;
    }

    // Helper: check if a child tab is locked
    isChildLocked(resourceUuid: string, tabId: string): boolean {
        const childId = `surgery-management:${resourceUuid}:${tabId}`;
        return this.lockedTabs.has(childId);
    }

    async disconnect() {
        console.log('Disconnecting socket...');

        this.autoReconnect = false; // Disabilita auto-reconnect

        if (this.retryTimeout) {
            clearTimeout(this.retryTimeout);
            this.retryTimeout = null;
        }

        // **CRITICAL FIX**: Release all held locks BEFORE disconnecting
        if (this.socket && this.socket.connected && this.lockedTabs.size > 0) {
            console.log(`Releasing ${this.lockedTabs.size} lock(s) before disconnect...`);

            const releasePromises = [];

            for (const [resourceId, lockInfo] of Array.from(this.lockedTabs.entries())) {
                console.log(` Releasing lock for resource: ${resourceId}`);

                // Create promise that resolves when lock is released or times out
                const releasePromise = new Promise<void>((resolve) => {
                    const timeout = setTimeout(() => {
                        resolve();
                    }, 2000); // 2 second timeout per lock

                    // Listen for confirmation
                    const confirmHandler = (data: any) => {
                        if (data.resourceId === resourceId) {
                            console.log(`Lock released: ${resourceId}`);
                            clearTimeout(timeout);
                            this.socket.off('lock:released', confirmHandler);
                            resolve();
                        }
                    };

                    this.socket.on('lock:released', confirmHandler);

                    // Emit release
                    this.socket.emit('lock:release', {
                        resourceId: resourceId,
                        mode: 'viewer'
                    });
                });

                releasePromises.push(releasePromise);
            }

            // Wait for all releases or timeouts (max 5 seconds total)
            try {
                await Promise.race([
                    Promise.all(releasePromises),
                    new Promise<void>(resolve => setTimeout(resolve, 5000))
                ]);
                console.log('[ SocketService] All locks released');
            } catch (error) {
                console.error('[SocketService] Error releasing locks:', error);
            }
        }

        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }

        this.currentResourceType = null;
        this.currentResourceUuid = null;
        this.lockedTabs.clear();
        this.retryCount = 0;

        this._updateConnectionStatus(ConnectionStatusEnum.DISCONNECTED);
    }

    getConnectionStatus() {
        return this.connectionStatus;
    }

    isConnected() {
        return this.socket && this.socket.connected;
    }

    // ==================== PRIVATE METHODS ====================

    _updateConnectionStatus(status: ConnectionStatus): void {
        const oldStatus = this.connectionStatus;
        this.connectionStatus = status;

        // NEW PATTERN: Direct property callback
        if (this.onConnectionChange) {
            this.onConnectionChange(status, oldStatus);
        }
        // OLD PATTERN: Callback object (fallback for backward compatibility)
        else if (this.callbacks.onConnectionChange) {
            this.callbacks.onConnectionChange(status, oldStatus);
        }
        // Silent fallback - callbacks will be registered later in useEffect
    }

    _scheduleRetry() {
        // Clear any existing retry timeout
        if (this.retryTimeout) {
            clearTimeout(this.retryTimeout);
            this.retryTimeout = null;
        }

        if (this.retryCount >= this.maxRetries) {
            console.error('[SocketService] Max retry attempts reached. Stopping reconnection.');
            this._notifyError('MAX_RETRIES_REACHED', 'Unable to connect after 5 attempts');
            this.autoReconnect = false;
            return;
        }

        const delay = this.retryDelays[this.retryCount];
        this.retryCount++;

        console.log(`[SocketService] ⏳ Retry ${this.retryCount}/${this.maxRetries} in ${delay}ms...`);

        this.retryTimeout = setTimeout(() => {
            console.log(`[SocketService] Attempting reconnection (${this.retryCount}/${this.maxRetries})`);
            this.retryTimeout = null;
            this.connect();
        }, delay);
    }

    _notifyError(type: string, message: string, data: any = null): void {
        if (this.callbacks.onError) {
            this.callbacks.onError({ type, message, data });
        }
    }

    _capitalize(str: string): string {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }
}

export default new SocketService();
